import { useDispatch, useSelector } from "react-redux";
import { Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@UI/Modal/Modal";
import { ModalErrorFallback } from "@UI/Modal/ModalErrorFallback";
import { WizardModal } from "@UI/WizardModal/WizardModal";
import { closeModal } from "@store/modalSlice";
import { getModalComponent, getModalBehavior, getModalMetadata } from "./modalRegistry";
import { ModalStackContext } from "./ModalStackContext";
import { ModalFooterContext } from "./ModalFooterContext";
import { ModalHeaderContext } from "./ModalHeaderContext";
import { ModalDescriptionContext } from "./ModalDescriptionContext";
import { ModalTitleBadgeContext } from "./ModalTitleBadgeContext";
import { MODAL_SIZE } from "@constants/appDefaults";
import { scrollLockManager } from "@utils/scrollLock";
import ErrorBoundary from "@utils/ErrorBoundary";

const BASE_Z_INDEX = 10003;

/** Fallback while lazy modal chunk loads */
const ModalFallback = () => null;

/** Wraps Modal with footer, header, and description context so modal content can register them. */
function ModalWithFooter({
  children,
  modalTitle,
  onClose,
  customStyles,
  disableBackdropClose,
  size,
  description: descriptionProp,
  zIndex,
  isTop,
  ariaHidden,
  inert,
}) {
  const [footer, setFooter] = useState(null);
  const [headerContent, setHeaderContent] = useState(null);
  const [descriptionContent, setDescriptionContent] = useState(null);
  const [titleBadge, setTitleBadge] = useState(null);
  const description = descriptionContent ?? descriptionProp;
  return (
    <ModalFooterContext.Provider value={[footer, setFooter]}>
      <ModalHeaderContext.Provider value={[headerContent, setHeaderContent]}>
        <ModalDescriptionContext.Provider value={[descriptionContent, setDescriptionContent]}>
          <ModalTitleBadgeContext.Provider value={[titleBadge, setTitleBadge]}>
            <Modal
              title={modalTitle}
              onClose={onClose}
              customStyles={customStyles}
              disableBackdropClose={disableBackdropClose}
              size={size}
              description={description}
              headerContent={headerContent}
              titleBadge={titleBadge}
            zIndex={zIndex}
            isTop={isTop}
            ariaHidden={ariaHidden}
            inert={inert}
            footer={footer}
          >
            {children}
          </Modal>
        </ModalTitleBadgeContext.Provider>
        </ModalDescriptionContext.Provider>
      </ModalHeaderContext.Provider>
    </ModalFooterContext.Provider>
  );
}

/**
 * ModalContainer Component
 *
 * Renders all modals in the stack from the registry. Each layer gets its own
 * z-index and ModalStackContext so useModal() returns that layer's entry.
 * Scroll lock is managed here at stack level: lock when stack goes from 0 to 1+, unlock when it goes to 0.
 */
const ModalContainer = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const stack = useSelector((state) => state.modal.stack);
  const prevStackLengthRef = useRef(0);

  useEffect(() => {
    const prev = prevStackLengthRef.current;
    const curr = stack.length;
    prevStackLengthRef.current = curr;

    if (curr > 0 && prev === 0) {
      scrollLockManager.lock();
    } else if (curr === 0 && prev > 0) {
      scrollLockManager.unlock();
    }
  }, [stack.length]);

  // Single Escape listener at stack level: close top modal unless it has disableBackdropClose
  useEffect(() => {
    if (stack.length === 0) return;

    const handleEscape = (e) => {
      if (e.key !== "Escape") return;
      const top = stack[stack.length - 1];
      if (top && !top.metadata?.disableBackdropClose) {
        dispatch(closeModal());
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [stack, dispatch]);

  if (stack.length === 0) {
    return null;
  }

  const handleClose = () => dispatch(closeModal());

  return (
    <>
      {stack.map((entry, index) => {
        const zIndex = BASE_Z_INDEX + index;
        const isTop = index === stack.length - 1;
        const ModalComponent = getModalComponent(entry.type);

        if (!ModalComponent) {
          console.warn(`Modal component for type "${entry.type}" is not registered`);
          return null;
        }

        const { wizard: isWizardModal, renderOwnOverlay } = getModalBehavior(entry.type);
        const registryMeta = getModalMetadata(entry.type, t);
        const metadata = { ...registryMeta, ...(entry.metadata || {}) };
        const hasExplicitTitle =
          metadata.title != null && String(metadata.title).trim() !== "";
        const modalTitle =
          (hasExplicitTitle ? metadata.title : null) ??
          (metadata.titleKey && typeof t === "function" ? t(metadata.titleKey) : null) ??
          entry.type;
        const step = entry.step ?? 1;

        const contextValue = { entry, isTop, onClose: handleClose };

        const LazyContent = (
          <ModalStackContext.Provider value={contextValue}>
            <ErrorBoundary
              fallback={(errorMessage, reset) => (
                <ModalErrorFallback
                  error={errorMessage}
                  onClose={() => {
                    reset();
                    handleClose();
                  }}
                />
              )}
            >
              <Suspense fallback={<ModalFallback />}>
                <ModalComponent />
              </Suspense>
            </ErrorBoundary>
          </ModalStackContext.Provider>
        );

        if (renderOwnOverlay) {
          return (
            <div
              key={index}
              style={{ position: "fixed", inset: 0, zIndex }}
              aria-hidden={!isTop}
              inert={!isTop ? "" : undefined}
            >
              {LazyContent}
            </div>
          );
        }

        const totalSteps = metadata.totalSteps ?? (isWizardModal ? 2 : 1);
        const stepLabels = metadata.stepLabels || [];
        const useWizard = isWizardModal && (step > 1 || totalSteps > 1);

        if (useWizard && totalSteps > 1) {
          return (
            <WizardModal
              key={index}
              title={modalTitle}
              onClose={handleClose}
              currentStep={step}
              totalSteps={totalSteps}
              stepLabels={stepLabels}
              customStyles={metadata.customStyles || {}}
              disableBackdropClose={metadata.disableBackdropClose || false}
              size={metadata.size || MODAL_SIZE.MEDIUM}
              description={metadata.description}
              zIndex={zIndex}
              isTop={isTop}
            >
              {LazyContent}
            </WizardModal>
          );
        }

        return (
          <ModalWithFooter
            key={index}
            modalTitle={modalTitle}
            onClose={handleClose}
            customStyles={metadata.customStyles || {}}
            disableBackdropClose={metadata.disableBackdropClose || false}
            size={metadata.size || MODAL_SIZE.MEDIUM}
            description={metadata.description}
            zIndex={zIndex}
            isTop={isTop}
            ariaHidden={!isTop}
            inert={!isTop ? "" : undefined}
          >
            {LazyContent}
          </ModalWithFooter>
        );
      })}
    </>
  );
};

export default ModalContainer;
